/**
 * What a stale Drive URL shows in an identity-only tenant (§8, Phase E).
 *
 * ── Why an empty state and not a redirect ───────────────────────────────
 *
 * §8 asks for "a purpose-built empty state if the user reaches a stale Drive
 * URL", and that agrees with a rule this codebase already applies elsewhere:
 * *a URL a viewer cannot use is refused in words, not redirected* (CLAUDE.md,
 * on the budget pages). A bookmark, a pasted link or the browser Back button
 * should say what happened. Silently teleporting to the launcher leaves someone
 * who typed a Drive URL wondering whether the click registered.
 *
 * ── The copy is doing real work ─────────────────────────────────────────
 *
 * The failure mode this whole feature has to avoid is reading as a lockout or a
 * suspension. It is neither: the account is intact, every organization still
 * works, and every file that was ever in this tenant is still there — the switch
 * is reversible with no repair pass (§9), so nothing has been lost. So the
 * notice says what IS available before it says what is not, and it never uses
 * "suspended", "disabled", "blocked" or "no access".
 *
 * It also does NOT say "ask your administrator to enable your drive". That is a
 * platform-admin setting on a deployment configured this way deliberately; a
 * call to action nobody will action is worse than none.
 *
 * ── Shared by both shells ───────────────────────────────────────────────
 *
 * Desktop `App.tsx` and `MobileShell` mount separate trees, so this lives in
 * `apps/web/src/components` where both reach it rather than being written twice
 * with copy that drifts.
 */
import { useTranslation } from '@papan/i18n'

export function IdentityOnlyNotice({
  /** Where "home" is for this tenant — the launcher, per `landingViewFor`. */
  homeHref,
  onGoHome,
}: {
  homeHref?: string
  onGoHome?: () => void
}) {
  const { t } = useTranslation('core-ui')

  return (
    <div
      className="flex-1 flex items-center justify-center p-8"
      data-testid="identity-only-notice"
    >
      <div className="max-w-md text-center">
        <div className="mx-auto mb-4 h-12 w-12 rounded-2xl themed-bg-tertiary flex items-center justify-center">
          {/* Inline, like the console's controls: this package should not pull
              an icon library in for one glyph. A folder with a soft slash —
              "not here", not "forbidden". */}
          <svg
            viewBox="0 0 24 24" width={24} height={24} aria-hidden
            fill="none" stroke="currentColor" strokeWidth={1.5}
            strokeLinecap="round" strokeLinejoin="round"
            className="themed-text-muted"
          >
            <path d="M3 7.5A1.5 1.5 0 0 1 4.5 6h4l2 2h7A1.5 1.5 0 0 1 19 9.5v7a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 3 16.5v-9Z" />
            <path d="M4 20 20 4" className="opacity-60" />
          </svg>
        </div>

        <h2 className="themed-text text-base font-medium mb-2">
          {t('identityOnly.driveTitle', { defaultValue: 'There is no drive here' })}
        </h2>

        <p className="themed-text-secondary text-sm leading-relaxed">
          {t('identityOnly.driveBody', {
            defaultValue:
              'This account is set up for working inside organizations and shared apps rather than keeping files of its own. Your account, your organizations and everything shared with you are unaffected.',
          })}
        </p>

        <p className="themed-text-muted text-xs mt-3">
          {t('identityOnly.driveSwitch', {
            defaultValue:
              'If you belong to an organization, switch to it from the account menu to open its drive.',
          })}
        </p>

        {/* An `<a>` ONLY when there is a real `href`, otherwise a `<button>`.
            It shipped as an unconditional anchor, and the desktop shell passes
            `onGoHome` alone — so it rendered `<a>` with no `href`, which is not
            focusable, not keyboard-activatable, and is exposed to assistive
            tech as plain text rather than a control. The only action on the
            screen a person lands on when their drive is gone was reachable by
            mouse and nothing else. Caught in a browser a11y snapshot, where it
            read as `StaticText`, not `link`. */}
        {homeHref ? (
          <a
            href={homeHref}
            onClick={onGoHome && ((e) => { e.preventDefault(); onGoHome() })}
            className="inline-block mt-5 px-4 py-2 text-xs font-medium rounded-lg themed-btn-primary"
            data-testid="identity-only-notice-home"
          >
            {t('identityOnly.driveGoHome', { defaultValue: 'Go to apps' })}
          </a>
        ) : onGoHome ? (
          <button
            type="button"
            onClick={onGoHome}
            className="inline-block mt-5 px-4 py-2 text-xs font-medium rounded-lg themed-btn-primary"
            data-testid="identity-only-notice-home"
          >
            {t('identityOnly.driveGoHome', { defaultValue: 'Go to apps' })}
          </button>
        ) : null}
      </div>
    </div>
  )
}
